// src/pages/AdminServices.jsx
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Services, api } from "../utils/api"; // Services.list(), api(path,{method,data})

const LEVELS = [
  { value: "tinh", label: "Tỉnh" },
  { value: "huyen", label: "Huyện" },
  { value: "xa", label: "Xã" },
];
const labelOf = (v) => (v === "tinh" ? "Tỉnh" : v === "huyen" ? "Huyện" : v === "xa" ? "Xã" : "");

const initialForm = { id: "", name: "", level: "" };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [autoId, setAutoId] = useState(true); // cho phép tự điền STT gợi ý

  // 1) Load danh sách từ BE
  const loadServices = async () => {
    try {
      const res = await Services.list(); // trả {items, counts} hoặc mảng
      const data = Array.isArray(res) ? res : res?.items;
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error("Không tải được danh sách dịch vụ");
      setItems([]);
    }
  };
  useEffect(() => { loadServices(); }, []);

  // 2) Sort theo STT tăng dần
  const sorted = useMemo(() => {
    const a = [...items];
    a.sort((x, y) => (Number(x.id) || 0) - (Number(y.id) || 0));
    return a;
  }, [items]);

  // 3) Tìm STT trống nhỏ nhất
  const nextFreeId = useMemo(() => {
    const used = new Set(items.map((x) => Number(x.id)));
    let i = 1;
    while (used.has(i)) i++;
    return i;
  }, [items]);

  // 4) Tự gợi ý STT khi vào trang / khi ấn Hàng mới / lưu xong
  useEffect(() => {
    if (autoId) setForm({ id: nextFreeId, name: "", level: "" });
  }, [nextFreeId, autoId]);

  // 5) Gõ STT: nếu trùng thì nạp để sửa, nếu mới thì reset name/level
  const onChangeId = (e) => {
    const val = e.target.value;
    setAutoId(false); // người dùng can thiệp → tắt auto
    if (!val) return setForm((s) => ({ ...s, id: "" }));

    const n = Number(val);
    if (Number.isNaN(n)) return setForm((s) => ({ ...s, id: val }));

    const found = items.find((x) => Number(x.id) === n);
    if (found) {
      setForm({ id: found.id, name: found.name || "", level: found.level || "" });
    } else {
      setForm({ id: n, name: "", level: "" });
    }
  };

  const onEdit = (row) => {
    setAutoId(false);
    setForm({ id: row.id, name: row.name || "", level: row.level || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onNew = () => {
    setAutoId(true);                // bật lại auto → effect sẽ tự fill nextFreeId
    setForm(initialForm);           // clear form để effect chạy
  };

  // 6) Lưu (UPSERT)
  const onSave = async () => {
    const id = Number(form.id);
    const name = String(form.name || "").trim();
    const level = String(form.level || "").trim();

    if (!id || !name || !level) {
      toast.error("Nhập đủ STT, Tên dịch vụ và Khu vực");
      return;
    }
    if (!["tinh", "huyen", "xa"].includes(level)) {
      toast.error("Khu vực chỉ được: Tỉnh / Huyện / Xã");
      return;
    }

    try {
      setLoading(true);
      await api("/admin/services/upsert", {
        method: "POST",
        data: { id, name, level },
      });
      toast.success("Đã lưu dịch vụ");
      await loadServices();
      setAutoId(true);              // sau khi load lại, tự nhảy tới STT trống tiếp theo
      setForm(initialForm);
    } catch (e) {
      console.error(e);
      toast.error("Lưu không thành công");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-2xl font-semibold">Quản lý dịch vụ</h2>

      {/* Form: 1 hàng duy nhất */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-2">
          <label className="block text-sm mb-1">STT</label>
          <input
            name="id"
            value={form.id}
            onChange={onChangeId}
            className="w-full border rounded px-3 py-2"
            inputMode="numeric"
            placeholder={`${nextFreeId}`}
          />
        </div>

        <div className="col-span-6">
          <label className="block text-sm mb-1">Tên dịch vụ</label>
          <input
            name="name"
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div className="col-span-3">
          <label className="block text-sm mb-1">Khu vực</label>
          <select
            name="level"
            value={form.level}
            onChange={(e) => setForm((s) => ({ ...s, level: e.target.value }))}
            className="w-full border rounded px-3 py-2 bg-white"
          >
            <option value="">-- Chọn khu vực --</option>
            {LEVELS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="col-span-12 flex gap-2">
          <button
            onClick={onSave}
            disabled={loading}
            className="bg-red-600 text-white px-4 py-2 rounded"
          >
            {loading ? "Đang lưu..." : "Lưu"}
          </button>
          <button onClick={onNew} className="border px-4 py-2 rounded">
            Hàng mới
          </button>
        </div>
      </div>

      {/* Bảng danh sách */}
      <div className="overflow-auto rounded border">
        <table className="w-full text-left">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2">STT</th>
              <th className="px-3 py-2">Tên dịch vụ</th>
              <th className="px-3 py-2">Khu vực</th>
              <th className="px-3 py-2 w-24">Sửa</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-3 py-2">{r.id}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2">{labelOf(r.level)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => onEdit(r)} className="text-red-600 underline">
                    Sửa
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                  Không có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
